import { TransactionState, TransactionStateName, TransactionTrigger, TransactionTriggerName } from '@frontend/transaction/types';
import { PossibleTrigger } from '@frontend/workflow/types';
import type { ReactNode } from 'react';

import { PossibleTriggerDisplay, PossibleTriggerGroup, PossibleTriggerLabels, fallbackPossibleTriggerDisplay } from '../../../workflow/possible-trigger-labels';

interface TransactionPossibleTriggerRule {
    trigger: TransactionTriggerName;
    nextState?: TransactionStateName | null;
    group: Exclude<PossibleTriggerGroup, 'other'>;
    label: ReactNode;
}

/**
 * Specific (trigger + next_state) rules first, then any-next-state action rules.
 */
const TRANSACTION_POSSIBLE_TRIGGER_RULES: TransactionPossibleTriggerRule[] = [
    {
        trigger: TransactionTriggerName.TRANSACTION_PROCESS,
        nextState: TransactionStateName.TRANSACTION_DROP_OFF_PENDING,
        group: 'action',
        label: PossibleTriggerLabels.MARK_READY_FOR_DROP_OFF
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_DROPPED_OFF,
        nextState: TransactionStateName.TRANSACTION_PICK_UP_PENDING,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_DROPPED_OFF
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_PICKED_UP,
        nextState: TransactionStateName.TRANSACTION_COMPLETED,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_PICKED_UP
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_PICKED_UP,
        nextState: TransactionStateName.TRANSACTION_IN_TRANSIT,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_PICKED_UP_IN_TRANSIT
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_CANCELLED,
        nextState: TransactionStateName.TRANSACTION_CANCELLED,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_CANCELLED
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_FAILED,
        nextState: TransactionStateName.TRANSACTION_FAILED,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_FAILED
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_LOST,
        nextState: TransactionStateName.TRANSACTION_LOST,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_LOST
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_PROCESSED,
        nextState: TransactionStateName.TRANSACTION_COMPLETED,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_COMPLETED
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_PROCESSED_WITH_FAILURES,
        nextState: TransactionStateName.TRANSACTION_COMPLETED_WITH_FAILURES,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_COMPLETED_WITH_FAILURES
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_PICK_UP,
        group: 'action',
        label: PossibleTriggerLabels.PICK_UP
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_DROP_OFF,
        group: 'action',
        label: PossibleTriggerLabels.DROP_OFF
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_CANCEL,
        group: 'action',
        label: PossibleTriggerLabels.CANCEL
    },
    {
        trigger: TransactionTriggerName.TRANSACTION_COMPLETE,
        group: 'action',
        label: PossibleTriggerLabels.COMPLETE
    }
];

export function getTransactionPossibleTriggerDisplay(
    possibleTrigger: PossibleTrigger<TransactionTriggerName | string, TransactionStateName | string | null>
): PossibleTriggerDisplay {
    const match = TRANSACTION_POSSIBLE_TRIGGER_RULES.find((rule) => {
        if (rule.trigger !== possibleTrigger.trigger) {
            return false;
        }
        if (rule.nextState === undefined) {
            return true;
        }
        return rule.nextState === possibleTrigger.next_state;
    });

    if (match) {
        return { group: match.group, display: match.label };
    }

    const triggerDisplay = TransactionTrigger.getByValue(possibleTrigger.trigger as TransactionTriggerName)?.displayName ?? possibleTrigger.trigger;
    const nextStateDisplay =
        possibleTrigger.next_state == null
            ? null
            : (TransactionState.getByValue(possibleTrigger.next_state as TransactionStateName)?.displayName ?? possibleTrigger.next_state);

    return {
        group: 'other',
        display: fallbackPossibleTriggerDisplay(triggerDisplay, nextStateDisplay)
    };
}
